import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { DashboardLayout } from "@/components/DashboardLayout";
import { KCard } from "@/components/KCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  Loader2, Package, Plus, Trash2, Edit2, Check, X, ArrowLeft, ToggleLeft, ToggleRight, Tag,
} from "lucide-react";
import { useAuth } from "@/auth/AuthContext";
import {
  getMockTestBundles,
  getMockTests,
  createMockTestBundle,
  updateMockTestBundle,
  deleteMockTestBundle,
  type MockTestBundle,
  type MockTest,
} from "@/api/lms/mock-tests";
import { cn } from "@/lib/utils";

type BundleFormState = {
  title: string;
  description: string;
  originalPrice: number;
  discountedPrice: number;
  currency: string;
  mockTestIds: string[];
};

const EMPTY_FORM: BundleFormState = {
  title: "",
  description: "",
  originalPrice: 0,
  discountedPrice: 0,
  currency: "BDT",
  mockTestIds: [],
};

export default function AdminMockTestBundlesPage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<BundleFormState>({ ...EMPTY_FORM });

  const { data: bundles = [], isLoading: bundlesLoading } = useQuery({
    queryKey: ["mockTestBundles", "admin"],
    queryFn: () => getMockTestBundles(false), // all, including inactive
  });

  const { data: testsData } = useQuery({
    queryKey: ["mockTests", "all"],
    queryFn: () => getMockTests({ page: 1, limit: 100 }),
  });

  const eligibleTests = (testsData?.items ?? []).filter((t: MockTest) => t.bundleEligible !== false && t.isPublished);

  // Price lookup across ALL tests (not just eligible) so editing an existing
  // bundle still totals correctly if a test later became ineligible.
  const priceById = useMemo(() => {
    const map = new Map<string, number>();
    for (const t of testsData?.items ?? []) map.set(t.id, Number(t.price ?? 0));
    return map;
  }, [testsData]);

  // Original price = sum of the selected tests' individual prices.
  const computedOriginalPrice = useMemo(
    () => form.mockTestIds.reduce((sum, id) => sum + (priceById.get(id) ?? 0), 0),
    [form.mockTestIds, priceById],
  );

  const createMutation = useMutation({
    mutationFn: () => createMockTestBundle({ ...form, originalPrice: computedOriginalPrice, mockTestIds: form.mockTestIds }),
    onSuccess: () => {
      toast.success("Bundle created!");
      queryClient.invalidateQueries({ queryKey: ["mockTestBundles"] });
      setShowForm(false);
      setForm({ ...EMPTY_FORM });
    },
    onError: (e: any) => toast.error(e?.message ?? "Failed to create bundle"),
  });

  const updateMutation = useMutation({
    mutationFn: () => updateMockTestBundle(editingId!, { ...form, originalPrice: computedOriginalPrice, mockTestIds: form.mockTestIds }),
    onSuccess: () => {
      toast.success("Bundle updated!");
      queryClient.invalidateQueries({ queryKey: ["mockTestBundles"] });
      setEditingId(null);
      setShowForm(false);
      setForm({ ...EMPTY_FORM });
    },
    onError: (e: any) => toast.error(e?.message ?? "Failed to update bundle"),
  });

  const toggleActiveMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      updateMockTestBundle(id, { isActive }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mockTestBundles"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Failed to update"),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteMockTestBundle(id),
    onSuccess: () => {
      toast.success("Bundle deleted");
      queryClient.invalidateQueries({ queryKey: ["mockTestBundles"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Failed to delete"),
  });

  if (!user || user.role !== "admin") return null;

  function startEdit(bundle: MockTestBundle) {
    setEditingId(bundle.id);
    setForm({
      title: bundle.title,
      description: bundle.description ?? "",
      originalPrice: Number(bundle.originalPrice),
      discountedPrice: Number(bundle.discountedPrice),
      currency: bundle.currency,
      mockTestIds: bundle.items?.map(i => i.mockTestId) ?? [],
    });
    setShowForm(true);
  }

  function cancelForm() {
    setShowForm(false);
    setEditingId(null);
    setForm({ ...EMPTY_FORM });
  }

  function toggleTestInBundle(id: string) {
    setForm(prev => ({
      ...prev,
      mockTestIds: prev.mockTestIds.includes(id)
        ? prev.mockTestIds.filter(x => x !== id)
        : [...prev.mockTestIds, id],
    }));
  }

  const discountPct = computedOriginalPrice > 0
    ? Math.round((1 - form.discountedPrice / computedOriginalPrice) * 100)
    : 0;

  return (
    <DashboardLayout>
      <div className="dash-page">
        {/* Header */}
        <div className="dash-page-header mb-6">
          <div className="flex items-center gap-3 min-w-0">
            <Button variant="ghost" size="sm" className="text-muted-foreground -ml-2 h-8 shrink-0" asChild>
              <Link to="/mock-tests"><ArrowLeft className="size-4 mr-1" />Mock Tests</Link>
            </Button>
            <div className="min-w-0">
              <h1 className="dash-page-title flex items-center gap-2 truncate">
                <Package className="text-primary size-6 shrink-0" />
                Mock Test Bundle Manager
              </h1>
              <p className="dash-page-subtitle truncate">
                Create discounted bundle deals that grant access to multiple mock tests at once
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button size="sm" asChild variant="outline" className="rounded-xl">
              <Link to="/admin/mock-test-purchases">Manage Purchases</Link>
            </Button>
            <Button
              size="sm"
              className="rounded-xl font-bold"
              onClick={() => { setEditingId(null); setForm({ ...EMPTY_FORM }); setShowForm(true); }}
            >
              <Plus className="size-4 mr-1" /> New Bundle
            </Button>
          </div>
        </div>

        {/* Create / Edit Form */}
        {showForm && (
          <KCard className="p-5 space-y-5 border-primary/30">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold text-sm">
                {editingId ? "Edit Bundle" : "Create New Bundle"}
              </h2>
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={cancelForm}>
                <X className="size-4" />
              </Button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Bundle Title *</Label>
                <Input
                  placeholder="e.g. JLPT N5 Complete Pack"
                  className="h-8 text-sm rounded-xl"
                  value={form.title}
                  onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Description</Label>
                <Input
                  placeholder="Short description…"
                  className="h-8 text-sm rounded-xl"
                  value={form.description}
                  onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Original Price (BDT)</Label>
                <Input
                  type="number"
                  className="h-8 text-sm rounded-xl bg-muted/40 cursor-not-allowed"
                  value={computedOriginalPrice}
                  readOnly
                  tabIndex={-1}
                />
                <p className="text-[10px] text-muted-foreground">
                  Auto-calculated from the {form.mockTestIds.length} selected test{form.mockTestIds.length !== 1 ? "s" : ""}.
                </p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">
                  Discounted Price (BDT)
                  {discountPct > 0 && (
                    <span className="ml-2 text-emerald-600 font-bold">{discountPct}% off</span>
                  )}
                </Label>
                <Input
                  type="number" min={0} step={10}
                  className="h-8 text-sm rounded-xl"
                  value={form.discountedPrice}
                  onChange={e => setForm(p => ({ ...p, discountedPrice: Math.max(0, Number(e.target.value) || 0) }))}
                />
              </div>
            </div>

            {/* Test selection */}
            <div className="space-y-2">
              <Label className="text-xs font-semibold">
                Include Mock Tests ({form.mockTestIds.length} selected)
              </Label>
              {eligibleTests.length === 0 ? (
                <p className="text-xs text-muted-foreground">No bundle-eligible published tests found. Set &quot;Bundle Eligible&quot; in the test editor.</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-64 overflow-y-auto pr-1">
                  {eligibleTests.map((t: MockTest) => {
                    const selected = form.mockTestIds.includes(t.id);
                    return (
                      <button
                        key={t.id}
                        type="button"
                        onClick={() => toggleTestInBundle(t.id)}
                        className={cn(
                          "flex items-center gap-2 px-3 py-2 rounded-xl border text-left text-xs transition-all",
                          selected
                            ? "bg-primary/10 border-primary text-primary font-semibold"
                            : "border-border text-muted-foreground hover:border-primary/50 hover:text-foreground"
                        )}
                      >
                        <div className={cn(
                          "w-4 h-4 rounded border shrink-0 flex items-center justify-center transition-all",
                          selected ? "bg-primary border-primary" : "border-border"
                        )}>
                          {selected && <Check className="size-2.5 text-white" />}
                        </div>
                        <div className="min-w-0">
                          <div className="truncate font-medium">{t.title || t.level}</div>
                          <div className="text-[10px] text-muted-foreground">
                            {Number(t.price ?? 0) === 0 ? "Free" : `${t.currency ?? "BDT"} ${t.price}`} · {t.attemptLimit ?? 1} attempt/purchase
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="flex gap-2 pt-1">
              <Button
                className="rounded-xl font-bold text-xs h-8 px-5"
                disabled={!form.title.trim() || form.mockTestIds.length === 0 || createMutation.isPending || updateMutation.isPending}
                onClick={() => editingId ? updateMutation.mutate() : createMutation.mutate()}
              >
                {(createMutation.isPending || updateMutation.isPending)
                  ? <Loader2 className="animate-spin size-3.5 mr-1" />
                  : <Check className="size-3.5 mr-1" />
                }
                {editingId ? "Update Bundle" : "Create Bundle"}
              </Button>
              <Button variant="outline" className="rounded-xl text-xs h-8" onClick={cancelForm}>
                Cancel
              </Button>
            </div>
          </KCard>
        )}

        {/* Bundles List */}
        {bundlesLoading ? (
          <div className="dash-empty-state flex flex-row items-center gap-2 text-muted-foreground text-sm">
            <Loader2 className="animate-spin size-5" /> Loading bundles…
          </div>
        ) : bundles.length === 0 ? (
          <div className="dash-empty-state">
            <Package className="size-12 mx-auto opacity-25 mb-4" />
            <p className="text-sm">No bundles yet. Create your first bundle to offer students a discounted package.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {bundles.map((bundle: MockTestBundle) => {
              const savings = Number(bundle.originalPrice) - Number(bundle.discountedPrice);
              const discPct = bundle.originalPrice > 0
                ? Math.round(savings / Number(bundle.originalPrice) * 100)
                : 0;

              return (
                <KCard key={bundle.id} className={cn("p-5", !bundle.isActive && "opacity-60")}>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-sm">{bundle.title}</h3>
                        {!bundle.isActive && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 border border-amber-200 text-amber-700 font-bold uppercase">Inactive</span>
                        )}
                        {discPct > 0 && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-50 border border-emerald-200 text-emerald-700 font-bold">
                            <Tag className="size-2.5 inline mr-0.5" />{discPct}% OFF
                          </span>
                        )}
                      </div>
                      {bundle.description && (
                        <p className="text-xs text-muted-foreground mt-0.5">{bundle.description}</p>
                      )}
                      <div className="flex items-center gap-3 mt-2 text-xs">
                        <span className="line-through text-muted-foreground">
                          {bundle.currency} {Number(bundle.originalPrice).toLocaleString()}
                        </span>
                        <span className="font-bold text-primary text-sm">
                          {bundle.currency} {Number(bundle.discountedPrice).toLocaleString()}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-1.5 mt-3">
                        {bundle.items?.map(item => (
                          <span
                            key={item.id}
                            className="text-[10px] px-2 py-0.5 rounded-full bg-muted/60 border border-border font-medium text-muted-foreground"
                          >
                            {item.mockTest?.title || item.mockTest?.level || "Test"}
                          </span>
                        ))}
                        {(!bundle.items || bundle.items.length === 0) && (
                          <span className="text-[10px] text-muted-foreground italic">No tests assigned</span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-1.5 shrink-0">
                      <Button
                        variant="ghost" size="sm"
                        className="h-7 px-2 text-xs rounded-lg text-muted-foreground hover:text-foreground"
                        onClick={() => toggleActiveMutation.mutate({ id: bundle.id, isActive: !bundle.isActive })}
                        disabled={toggleActiveMutation.isPending}
                      >
                        {bundle.isActive
                          ? <ToggleRight className="size-4 text-emerald-500" />
                          : <ToggleLeft className="size-4" />
                        }
                      </Button>
                      <Button
                        variant="ghost" size="sm"
                        className="h-7 w-7 p-0 rounded-lg text-muted-foreground hover:text-foreground"
                        onClick={() => startEdit(bundle)}
                      >
                        <Edit2 className="size-3.5" />
                      </Button>
                      <Button
                        variant="ghost" size="sm"
                        className="h-7 w-7 p-0 rounded-lg hover:bg-destructive/10 hover:text-destructive text-muted-foreground"
                        disabled={deleteMutation.isPending}
                        onClick={() => {
                          if (window.confirm(`Delete bundle "${bundle.title}"?`)) {
                            deleteMutation.mutate(bundle.id);
                          }
                        }}
                      >
                        <Trash2 className="size-3.5" />
                      </Button>
                    </div>
                  </div>
                </KCard>
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
